import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Building2,
    Layers,
    Settings,
    CheckCircle2,
    Plus,
    Trash2,
    ArrowLeft,
    ArrowRight,
    Loader2,
    GitBranch,
    PlusCircle
} from 'lucide-react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Modal from '../components/ui/Modal';

import { useToast } from '../hooks/useToast';
import { client, fetchStoresWhereStaffAssigned, fetchInventoryForStoreSales, fetchServicesForStoreSale } from '../apiHelper';


const STEPS = [
    { id: 1, name: 'Location & Header', icon: Building2 },
    { id: 2, name: 'BOM Line Components', icon: Layers },
    { id: 3, name: 'Routing Operations', icon: Settings },
    { id: 4, name: 'Review Master Blueprint', icon: CheckCircle2 }
];

const WORKCENTER_RESOURCE_TYPES = [
    // Machining & Subtractive (Metalworking, Woodworking, Plastics)
    { label: "CNC / Machining Center", value: "cnc_machining" },
    { label: "Cutting & Sawing Station", value: "cutting_sawing" },
    { label: "Molding & Casting Unit", value: "molding_casting" },

    // Fabrication & Joining (Automotive, Heavy Machinery, Structures)
    { label: "Welding & Fabrication Bay", value: "welding_fab" },
    { label: "Assembly Line / Workcell", value: "assembly_line" },
    { label: "Surface Treatment & Coating (Painting/Anodizing)", value: "surface_treatment" },

    // Process & Thermal Industries (Food, Pharma, Chemical, Metallurgy)
    { label: "Mixing / Blending Vessel", value: "mixing_blending" },
    { label: "Oven / Furnace / Kiln (Thermal)", value: "thermal_processing" },
    { label: "Bottling & Packaging Line", value: "packaging_bottling" },

    // Inspection, Lab, & Specialized Services
    { label: "Quality Assurance & Inspection Station", value: "qa_inspection" },
    { label: "Testing & Calibration Lab", value: "testing_lab" },

    // Human Labor / Manual Operations
    { label: "Manual Benchwork / Hand Assembly", value: "manual_labor" }
];

const ConfigureProductSpecs = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Track localized context configs
    const [user, setUser] = useState(null);
    const [selectedStore, setSelectedStore] = useState('');

    // Master Selection Data Options
    const [storeOptions, setStoreOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [workcenterOptions, setWorkcenterOptions] = useState([]);

    // Inline Workcenter Creator Modal State
    const [isWcModalOpen, setIsWcModalOpen] = useState(false);
    const [wcSubmitting, setWcSubmitting] = useState(false);
    const [newWorkcenter, setNewWorkcenter] = useState({
        name: '',
        resource_type: '',
        capacity_per_hour: '',
        cost_per_hour: '',
        description: '',
    });

    // Master Form Payload State (matching SQLAlchemy Schemas)
    const [formData, setFormData] = useState({
        storeId: '',
        catentryId: '',
        bomDescription: '',
        bomVersion: 1,
        routingDescription: '',
        routingVersion: 1,
        bomId: '',
        routingId: '',
        bomLines: [],
        routingOperations: []
    });

    // Dynamic array item form line inputs
    const [currentBomLine, setCurrentBomLine] = useState({
        componentCatentryId: '',
        quantity: '',
        unitMeasure: 'PCE',
        scrapFactor: 0.0,
        description: ''
    });

    const [currentOperation, setCurrentOperation] = useState({
        workcenterId: '',
        sequence: '',
        description: '',
        setupTime: '',
        runTimePerUnit: '',
        laborCostPerHour: ''
    });

    const [formErrors, setFormErrors] = useState({});


    // Step 1: Initialize User Profile & Store Assignment Filters
    useEffect(() => {
        const initializeForm = async () => {
            setLoading(true);
            try {
                const storedUser = localStorage.getItem('user');
                const parsedUser = storedUser ? JSON.parse(storedUser) : null;
                setUser(parsedUser);

                const stores = await fetchStoresWhereStaffAssigned(parsedUser);

                if (stores && stores.length > 0) {
                    const mappedStores = stores.map(store => ({
                        label: store.identifier || `Branch ${store.store_id}`,
                        value: store.store_id.toString(),
                        storeData: store
                    }));

                    setStoreOptions(mappedStores);

                    // Pre-select first store location
                    const initialStoreId = mappedStores[0].value;
                    setSelectedStore(initialStoreId);
                    setFormData(prev => ({ ...prev, storeId: initialStoreId }));

                    // Fetch products available for this branch
                    await loadProductsForStore(initialStoreId, parsedUser);
                    await loadWorkcentersForStore(initialStoreId);
                } else {
                    setStoreOptions([]);
                    showToast('No branch locations assigned to your account profile.', 'error');
                }
            } catch (error) {
                console.error("Error loading assigned configurations:", error);
                showToast(error.response?.data?.detail || 'Failed to initialize location data.', 'error');
            } finally {
                setLoading(false);
            }
        };

        initializeForm();
    }, [showToast]);


    // 2. Fetch Catalog Items Available for a Selected Store
    const loadProductsForStore = async (storeId, currentUser) => {
        const userContext = currentUser || null;
        if (!userContext) return;

        try {
            const payload = {
                ...userContext,
                store_id: parseInt(storeId, 10),
                offset: 0,
                limit: 100000,
            };

            const [fetchedServices, fetchedInventory] = await Promise.all([
                fetchServicesForStoreSale(payload).catch(() => []),
                fetchInventoryForStoreSales(payload).catch(() => [])
            ]);

            const allLots = [...fetchedServices, ...fetchedInventory];
            const aggregatedProductsMap = new Map();

            allLots.forEach(lot => {
                if (lot?.catentry) {
                    const catentryId = lot.catentry.catentry_id;
                    if (!aggregatedProductsMap.has(catentryId)) {
                        aggregatedProductsMap.set(catentryId, {
                            label: `${lot.catentry.productname} (${lot.catentry.partnumber || 'No SKU'})`,
                            value: catentryId.toString(),
                            rawDetails: lot.catentry
                        });
                    }
                }
            });

            setProductOptions(Array.from(aggregatedProductsMap.values()));
        } catch (error) {
            console.error("Error aggregating products for location:", error);
            showToast(error.response?.data?.detail || 'Could not load target inventory for this branch.', 'error');
        }
    };

    // Isolated Workcenter population logic triggered safely on node transition contexts
    const loadWorkcentersForStore = async (storeId) => {
        try {
            // TODO perhaps we should enable creation of workcenters as part of the configuration workflow if none exist from the fetch below
            const response = await client.fetchData(`/manufacturing/work-centers/${storeId}`)
            // this logs an empty [] to console
            // console.log(response)
            const workcentersList = Array.isArray(response) ? response : (response?.workcenters || []);
            setWorkcenterOptions(workcentersList.map(w => ({
                label: `${w.name} (${WORKCENTER_RESOURCE_TYPES.find(t => t.value === w.resource_type)?.label || w.resource_type || 'Generic'})`,
                value: w.workcenter_id.toString()
            })));

        } catch (error) {
            console.error("Error contextualizing workcenter items:", error);
            showToast(error.response?.data?.detail || "Error contextualizing workcenter items.", "error");
        }
    };

    // Inline Submit Handler for dynamically provisioning new plant assets
    const handleCreateWorkcenter = async (e) => {
        e.preventDefault();
        if (!newWorkcenter.name || !newWorkcenter.resource_type) {
            showToast("Work Center Name and Resource Classification Type are required.", "error");
            return;
        }

        setWcSubmitting(true);
        try {
            const payload = {
                store_id: parseInt(formData.storeId, 10),
                name: newWorkcenter.name,
                resource_type: newWorkcenter.resource_type,
                capacity_per_hour: newWorkcenter.capacity_per_hour ? parseFloat(newWorkcenter.capacity_per_hour) : null,
                cost_per_hour: newWorkcenter.cost_per_hour ? parseFloat(newWorkcenter.cost_per_hour) : null,
                description: newWorkcenter.description || null,
                is_active: true
            };

            // console.log(payload);
            const response = await client.postData("/manufacturing/new-work-center", payload);
            if (response) {
                showToast(`Workcenter "${newWorkcenter.name} successfully setup on factory floor.`, "success");
    
                await loadWorkcentersForStore(formData.storeId);

                if (response.workcenter_id) {
                    setCurrentOperation(prev => ({ ...prev, workcenterId: response.workcenter_id.toString() }));
                }

                setNewWorkcenter({ name: '', resource_type: '', capacity_per_hour: '', cost_per_hour: '', description: '' });
                setIsWcModalOpen(false);
            }
        } catch (err) {
            console.error("Error creating inline manufacturing resource center.", err);
            showToast(err.response?.data?.detail || "Could not register production cell asset.", "error");
        } finally {
            setWcSubmitting(false);
        }
    };

    // Global unified handleInputChange system
    const handleInputChange = async (e) => {
        const { name, value } = e.target;

        if (name === "storeId") {
            setSelectedStore(value);

            setFormData(prev => ({
                ...prev,
                storeId: value,
                catentryId: '',
                bomId: '',
                routingId: '',
                bomLines: [],
                routingOperations: []
            }));
            setProductOptions([]);
            setWorkcenterOptions([]);

            if (value) {
                await loadProductsForStore(value, user);
                await loadWorkcentersForStore(value);
            }
        } else if (name === "catentryId") {
            setFormData(prev => ({
                ...prev,
                catentryId: value,
                bomId: '',
                routingId: '',
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Matrix Modifiers: BOM Lines
    const addBomLine = () => {
        if (!currentBomLine.componentCatentryId || !currentBomLine.quantity) {
            showToast("Component item selection and targeted base quantities are required.", "error");
            return;
        }
        setFormData(prev => ({
            ...prev,
            bomLines: [...prev.bomLines, {
                ...currentBomLine,
                quantity: parseFloat(currentBomLine.quantity),
                scrapFactor: parseFloat(currentBomLine.scrapFactor || 0) / 100
            }]
        }));
        setCurrentBomLine({ componentCatentryId: '', quantity: '', unitMeasure: 'PCE', scrapFactor: 0.0, description: '' });
    };

    const removeBomLine = (index) => {
        setFormData(prev => ({ ...prev, bomLines: prev.bomLines.filter((_, i) => i !== index) }));
    };

    // Matrix Modifiers: Workcenter Sequence Routing Paths
    const addOperation = () => {
        if (!currentOperation.workcenterId || !currentOperation.sequence) {
            showToast("Workcenter cell node and sequence placement indices are required.", "error");
            return;
        }
        setFormData(prev => ({
            ...prev,
            routingOperations: [
                ...prev.routingOperations,
                {
                    ...currentOperation,
                    sequence: parseInt(currentOperation.sequence, 10),
                    setupTime: currentOperation.setupTime ? parseFloat(currentOperation.setupTime) : null,
                    runTimePerUnit: currentOperation.runTimePerUnit ? parseFloat(currentOperation.runTimePerUnit) : null,
                    laborCostPerHour: currentOperation.laborCostPerHour ? parseFloat(currentOperation.laborCostPerHour) : null
                }
            ].sort((a, b) => a.sequence - b.sequence)
        }));
        setCurrentOperation({ workcenterId: '', sequence: '', description: '', setupTime: '', runTimePerUnit: '', laborCostPerHour: '' });
    };

    const removeOperation = (index) => {
        setFormData(prev => ({ ...prev, routingOperations: prev.routingOperations.filter((_, i) => i !== index) }));
    };

    const validateStep = () => {
        const errors = {};
        if (currentStep === 1) {
            if (!formData.storeId) errors.storeId = 'Structural branch location target context is required';
            if (!formData.catentryId) errors.catentryId = 'Manufactured tracking finished good is required';
        }
        if (currentStep === 2 && formData.bomLines.length === 0) {
            showToast("Please assign at least one component to this raw recipe structure.", "error");
            return false;
        }
        if (currentStep === 3 && formData.routingOperations.length === 0) {
            showToast("Please configure at least one sequential factory routing checkpoint.", "error");
            return false;
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
        if (validateStep()) setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    };

    const handleSubmitMasterConfiguration = async () => {
        setSubmitting(true);
        try {
            const payload = {
                catentry_id: parseInt(formData.catentryId, 10),
                store_id: parseInt(formData.storeId, 10),
                bom_header: {
                    version: parseInt(formData.bomVersion, 10),
                    description: formData.bomDescription || null,
                    is_active: true,
                    lines: formData.bomLines.map((line, index) => ({
                        component_catentry_id: parseInt(line.componentCatentryId, 10),
                        quantity: line.quantity,
                        unit_measure: line.unitMeasure,
                        scrap_factor: line.scrapFactor,
                        sequence: index + 1,
                        description: line.description || null
                    }))
                },
                routing_header: {
                    version: parseInt(formData.routingVersion, 10),
                    description: formData.routingDescription || null,
                    is_active: true,
                    operations: formData.routingOperations.map(op => ({
                        workcenter_id: parseInt(op.workcenterId, 10),
                        sequence: op.sequence,
                        description: op.description || null,
                        setup_time: op.setupTime,
                        run_time_per_unit: op.runTimePerUnit,
                        labor_cost_per_hour: op.laborCostPerHour
                    }))
                }
            };
            
            console.log(payload);
            const response = await client.postData("/manufacturing/create-bom-routing-headers", payload);
            if (response) {
                showToast("Engineering blueprints registered and verified successfully.", "success");
                // navigate('/manufacturing/dashboard');
            }
        } catch (error) {
            console.error("Master build deployment fault:", error);
            showToast(error.response?.data?.detail || "Could not publish engineering records.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const TargetProductLabel = useMemo(() => productOptions.find(o => o.value === formData.catentryId)?.label || 'Not Selected', [productOptions, formData.catentryId]);
    const TargetStoreLabel = useMemo(() => storeOptions.find(o => o.value === formData.storeId)?.label || 'Not Selected', [storeOptions, formData.storeId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
                <Loader2 size={36} className="animate-spin text-indigo-600" />
                <p className="text-zinc-500 text-sm font-medium">Synchronizing branch engineering pipelines...</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Engineering Master Configurations</h1>
                <p className="text-zinc-500 text-sm mt-1">Bind Bill of Materials recipes and track operational shop routing matrices by plant location.</p>
            </div>

            {/* Step Wizard Tracker */}
            <div className="mb-8 bg-white border border-zinc-200/80 rounded-xl p-4 shadow-sm flex justify-between items-center relative overflow-hidden">
                {STEPS.map((step, index) => {
                    const StepIcon = step.icon;
                    const isCompleted = currentStep > step.id;
                    const isActive = currentStep === step.id;

                    return (
                        <div key={step.id} className="flex items-center flex-1 last:flex-initial z-10">
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${isActive ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm' :
                                    isCompleted ? 'bg-emerald-50 border-emerald-200 text-emerald-600' :
                                        'bg-zinc-50 border-zinc-200 text-zinc-400'
                                    }`}>
                                    <StepIcon size={16} />
                                </div>
                                <div className="hidden md:block text-left">
                                    <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Step 0{step.id}</p>
                                    <p className={`text-sm font-semibold ${isActive ? 'text-indigo-600' : 'text-zinc-700'}`}>{step.name}</p>
                                </div>
                            </div>
                            {index < STEPS.length - 1 && (
                                <div className={`hidden md:block flex-1 h-px mx-4 ${isCompleted ? 'bg-emerald-200' : 'bg-zinc-200'}`} />
                            )}
                        </div>
                    );
                })}
            </div>

            <Card className="p-6 border-zinc-200/60 shadow-sm bg-white min-h-[320px] flex flex-col justify-between">

                {/* STEP 1: Branch Scope and Header Rules */}
                {currentStep === 1 && (
                    <div className="space-y-6 animate-fadeIn">
                        <div className="border-b border-zinc-100 pb-3">
                            <h3 className="text-lg font-bold text-zinc-900">Establish Reference Header Context</h3>
                            <p className="text-zinc-500 text-xs">Pick your active operations floor to isolate valid localized machining centers.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <SearchableSelectBox
                                name="storeId"
                                label="Operational Plant Location"
                                placeholder="Select tracking branch location..."
                                value={formData.storeId}
                                onChange={(e) => handleInputChange}
                                options={storeOptions}
                                error={formErrors.storeId}
                                required
                            />
                            <SearchableSelectBox
                                name="catentryId"
                                label="Manufactured Target Item Asset"
                                placeholder="Select item output target..."
                                value={formData.catentryId}
                                onChange={handleInputChange}
                                options={productOptions}
                                disabled={!formData.storeId}
                                error={formErrors.catentryId}
                                required
                            />
                        </div>
                    </div>
                )}

                {/* STEP 2: Ingredient Composition Table Ledger */}
                {currentStep === 2 && (
                    <div className="space-y-6 animate-fadeIn">
                        <div className="border-b border-zinc-100 pb-3">
                            <h3 className="text-lg font-bold text-zinc-900">Formulate Component Material Lines</h3>
                            <p className="text-zinc-500 text-xs">Allocate quantities and apply expected material shrink factors.</p>
                        </div>

                        <div className="p-4 bg-zinc-50 border border-zinc-200/60 rounded-xl grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                            <div className="md:col-span-5">
                                <SearchableSelectBox
                                    label="Component / Raw Sub-Assembly"
                                    placeholder="Search warehouse components..."
                                    value={currentBomLine.componentCatentryId}
                                    onChange={(e) => setCurrentBomLine(prev => ({ ...prev, componentCatentryId: e.target.value }))}
                                    options={productOptions}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Input
                                    label="Qty Required"
                                    type="number"
                                    value={currentBomLine.quantity}
                                    onChange={(e) => setCurrentBomLine(prev => ({ ...prev, quantity: e.target.value }))}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Input
                                    label="Scrap Factor %"
                                    type="number"
                                    value={currentBomLine.scrapFactor}
                                    onChange={(e) => setCurrentBomLine(prev => ({ ...prev, scrapFactor: e.target.value }))}
                                />
                            </div>
                            <div className="md:col-span-3">
                                <Button type="button" onClick={addBomLine} className="w-full mb-4 bg-zinc-900 hover:bg-zinc-800 text-white flex justify-center items-center gap-1.5 text-xs py-2.5">
                                    <Plus size={14} /> Log Ingredient
                                </Button>
                            </div>
                        </div>

                        <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                            <table className="w-full border-collapse text-left text-sm">
                                <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider border-b border-zinc-200">
                                    <tr>
                                        <th className="p-3 font-semibold">SKU Component Blueprint</th>
                                        <th className="p-3 font-semibold">Base Allocation</th>
                                        <th className="p-3 font-semibold">Waste Shrinkage</th>
                                        <th className="p-3 font-semibold text-right">Remove</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-100 text-zinc-700">
                                    {formData.bomLines.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="p-8 text-center text-zinc-400 text-xs font-medium">No component assets added to this configuration line array yet.</td>
                                        </tr>
                                    ) : (
                                        formData.bomLines.map((line, idx) => (
                                            <tr key={idx} className="hover:bg-zinc-50/60 transition-colors">
                                                <td className="p-3 font-medium text-zinc-900">{productOptions.find(p => p.value === line.componentCatentryId)?.label || 'Unknown Asset'}</td>
                                                <td className="p-3">{line.quantity} {line.unitMeasure}</td>
                                                <td className="p-3">{(line.scrapFactor * 100).toFixed(1)}%</td>
                                                <td className="p-3 text-right">
                                                    <button type="button" onClick={() => removeBomLine(idx)} className="text-rose-500 hover:text-rose-700 p-1">
                                                        <Trash2 size={15} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* STEP 3: Contextualized Shop Floor Sequence Runs */}
                {currentStep === 3 && (
                    <div className="space-y-6 animate-fadeIn">
                        <div className="border-b border-zinc-100 pb-3 flex justify-between items-end">
                            <div>
                                <h3 className="text-lg font-bold text-zinc-900">Map Flow Routing Sequence Steps</h3>
                                <p className="text-zinc-500 text-xs">Assign linear step sequences exclusively using active work centers within this facility branch.</p>
                            </div>
                            <Button
                                // variant="primary"
                                type="button"
                                size="sm"
                                onClick={() => setIsWcModalOpen(true)}
                                className="bg-indigo-50 hover:bg-indigo-100 !text-indigo-700 font-semibold border border-indigo-200 flex items-center gap-1.5 text-xs py-1.5"
                            >
                                <PlusCircle size={14} />New Work Center
                            </Button>
                        </div>

                        <div className="p-4 bg-zinc-50 border border-zinc-200/60 rounded-xl space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                                <div className="md:col-span-2">
                                    <Input
                                        label="Seq Index"
                                        type="number"
                                        placeholder="e.g. 10"
                                        value={currentOperation.sequence}
                                        onChange={(e) => setCurrentOperation(prev => ({ ...prev, sequence: e.target.value }))}
                                    />
                                </div>
                                <div className="md:col-span-5">
                                    <SearchableSelectBox
                                        label="Active Plant Workcenter"
                                        placeholder="Assign floor station center..."
                                        value={currentOperation.workcenterId}
                                        onChange={(e) => setCurrentOperation(prev => ({ ...prev, workcenterId: e.target.value }))}
                                        options={workcenterOptions}
                                    />
                                </div>
                                <div className="md:col-span-5">
                                    <Input
                                        label="Process Operation Text"
                                        placeholder="E.g., Automated high-precision edge router run..."
                                        value={currentOperation.description}
                                        onChange={(e) => setCurrentOperation(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end border-t border-zinc-200/60 pt-3">
                                <Input label="Setup Hours" type="number" placeholder="0.5" value={currentOperation.setupTime} onChange={(e) => setCurrentOperation(prev => ({ ...prev, setupTime: e.target.value }))} />
                                <Input label="Run Hours/Unit" type="number" placeholder="0.02" value={currentOperation.runTimePerUnit} onChange={(e) => setCurrentOperation(prev => ({ ...prev, runTimePerUnit: e.target.value }))} />
                                <Input label="Labor Rate ($/hr)" type="number" placeholder="32.50" value={currentOperation.laborCostPerHour} onChange={(e) => setCurrentOperation(prev => ({ ...prev, laborCostPerHour: e.target.value }))} />
                                <Button type="button" onClick={addOperation} className="!mb-4 bg-zinc-900 hover:bg-zinc-800 text-white flex justify-center items-center gap-1.5 text-xs py-2.5">
                                    <Plus size={14} /> Commit Step
                                </Button>
                            </div>
                        </div>

                        <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                            <table className="w-full border-collapse text-left text-sm">
                                <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider border-b border-zinc-200">
                                    <tr>
                                        <th className="p-3 font-semibold w-16">Sequence</th>
                                        <th className="p-3 font-semibold">Assigned Station Center</th>
                                        <th className="p-3 font-semibold">Process Directive Notes</th>
                                        <th className="p-3 font-semibold text-right">Delete</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-100 text-zinc-700">
                                    {formData.routingOperations.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="p-8 text-center text-zinc-400 text-xs font-medium">No sequenced factory workstation paths recorded yet.</td>
                                        </tr>
                                    ) : (
                                        formData.routingOperations.map((op, idx) => (
                                            <tr key={idx} className="hover:bg-zinc-50/60 transition-colors">
                                                <td className="p-3 font-bold text-indigo-600">#{op.sequence}</td>
                                                <td className="p-3 font-medium text-zinc-900">{workcenterOptions.find(w => w.value === op.workcenterId)?.label || 'Unknown Center'}</td>
                                                <td className="p-3 text-zinc-500 text-xs">{op.description || 'Linear shop path operation runtime stage.'}</td>
                                                <td className="p-3 text-right">
                                                    <button type="button" onClick={() => removeOperation(idx)} className="text-rose-500 hover:text-rose-700 p-1">
                                                        <Trash2 size={15} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* STEP 4: Review Summary Configuration */}
                {currentStep === 4 && (
                    <div className="space-y-6 animate-fadeIn">
                        <div className="border-b border-zinc-100 pb-3">
                            <h3 className="text-lg font-bold text-zinc-900">Verify Structural Assembly Blueprints</h3>
                            <p className="text-zinc-500 text-xs">Confirm cross-relational alignment schemas before publishing updates live.</p>
                        </div>

                        <div className="bg-zinc-50 border border-zinc-200/60 rounded-xl p-4 text-sm space-y-1">
                            <span className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-tight mb-1">
                                <GitBranch size={14} /> Core Manufacturing Coordinates
                            </span>
                            <p className="text-zinc-800 font-semibold text-base">{TargetProductLabel}</p>
                            <p className="text-zinc-500 text-xs">Allocated Production Plant Scope: <span className="text-zinc-700 font-medium">{TargetStoreLabel}</span></p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
                            <div className="border border-zinc-200 rounded-xl p-4 space-y-3 bg-white">
                                <h4 className="font-bold text-zinc-900 text-sm border-b border-zinc-100 pb-1.5 flex justify-between items-center">
                                    <span>Material Allocation Blueprint</span>
                                    <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-medium">Revision {formData.bomVersion}</span>
                                </h4>
                                <ul className="space-y-2 max-h-[180px] overflow-y-auto">
                                    {formData.bomLines.map((line, idx) => (
                                        <li key={idx} className="flex justify-between items-center text-zinc-700 border-b border-zinc-50 pb-1">
                                            <span>{productOptions.find(p => p.value === line.componentCatentryId)?.label.split(' (')[0]}</span>
                                            <span className="font-semibold text-zinc-900">{line.quantity} {line.unitMeasure}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="border border-zinc-200 rounded-xl p-4 space-y-3 bg-white">
                                <h4 className="font-bold text-zinc-900 text-sm border-b border-zinc-100 pb-1.5 flex justify-between items-center">
                                    <span>Workcenter Process Operations</span>
                                    <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-medium">Track Rev {formData.routingVersion}</span>
                                </h4>
                                <ul className="space-y-2 max-h-[180px] overflow-y-auto">
                                    {formData.routingOperations.map((op, idx) => (
                                        <li key={idx} className="flex gap-3 items-center text-zinc-700 border-b border-zinc-50 pb-1">
                                            <span className="font-bold text-indigo-600">#{op.sequence}</span>
                                            <div className="flex-1 truncate">
                                                <p className="font-medium text-zinc-900 truncate">{workcenterOptions.find(w => w.value === op.workcenterId)?.label}</p>
                                                <p className="text-[10px] text-zinc-400 truncate">{op.description || 'Standard Phase Run'}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer Controls */}
                <div className="flex justify-between items-center border-t border-zinc-100 pt-6 mt-8">
                    <button
                        type="button"
                        onClick={currentStep === 1 ? () => navigate(-1) : () => setCurrentStep(p => p - 1)}
                        disabled={submitting}
                        className="flex items-center gap-2 px-4 py-2 border border-zinc-200 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
                    >
                        <ArrowLeft size={16} />
                        {currentStep === 1 ? 'Cancel' : 'Back'}
                    </button>

                    {currentStep < STEPS.length ? (
                        <Button type="button" onClick={handleNext} className="flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 px-5 py-2 text-sm">
                            Continue <ArrowRight size={16} />
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            onClick={handleSubmitMasterConfiguration}
                            disabled={submitting}
                            className="flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 px-6 py-2 text-sm disabled:opacity-60 font-semibold"
                        >
                            {submitting ? (
                                <><Loader2 size={16} className="animate-spin" /> Committing Architecture Sheet...</>
                            ) : (
                                <>Save Master Engineering Blueprint <CheckCircle2 size={16} /></>
                            )}
                        </Button>
                    )}
                </div>
            </Card>
            <Modal 
                isOpen={isWcModalOpen} 
                onClose={() => setIsWcModalOpen(false)} 
                title="Provision New Plant Work Center"
                size="lg"
            >
                <form onSubmit={handleCreateWorkcenter} className="space-y-4">
                    <p className="text-xs text-zinc-500 mb-2">
                        Register a machine asset or cell line contextually assigned to <strong>{TargetStoreLabel}</strong>.
                    </p>
                    
                    <Input 
                        label="Work Center / Production Cell Name"
                        placeholder="e.g. Assembly Line A, Haas VF-2 CNC Router"
                        value={newWorkcenter.name}
                        onChange={(e) => setNewWorkcenter(prev => ({ ...prev, name: e.target.value }))}
                        required
                    />

                    <SearchableSelectBox 
                        label="Resource Category Verticals"
                        placeholder="Select machine/labor class classification..."
                        value={newWorkcenter.resource_type}
                        onChange={(e) => setNewWorkcenter(prev => ({ ...prev, resource_type: e.target.value }))}
                        options={WORKCENTER_RESOURCE_TYPES}
                        required
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input 
                            label="Capacity per Hour (Units)"
                            type="number"
                            placeholder="e.g. 150"
                            value={newWorkcenter.capacity_per_hour}
                            onChange={(e) => setNewWorkcenter(prev => ({ ...prev, capacity_per_hour: e.target.value }))}
                        />
                        <Input 
                            label="Operational Cost/Hr ($)"
                            type="number"
                            step="0.00001"
                            placeholder="e.g. 45.50"
                            value={newWorkcenter.cost_per_hour}
                            onChange={(e) => setNewWorkcenter(prev => ({ ...prev, cost_per_hour: e.target.value }))}
                        />
                    </div>

                    <Input 
                        label="Station Description / Capabilities"
                        placeholder="Specify machine tool tolerances, certification needs, or station constraints..."
                        value={newWorkcenter.description}
                        onChange={(e) => setNewWorkcenter(prev => ({ ...prev, description: e.target.value }))}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t border-zinc-100">
                        <Button 
                            type="button" 
                            variant="ghost" 
                            onClick={() => setIsWcModalOpen(false)}
                            disabled={wcSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            className="bg-indigo-600 text-white hover:bg-indigo-700"
                            disabled={wcSubmitting}
                        >
                            {wcSubmitting ? <Loader2 size={16} className="animate-spin" /> : "Deploy Production Resource"}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ConfigureProductSpecs;